import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';

/**
 * Primeira barra lateral — a mesma na caixa e na gestão: símbolo em cima,
 * o que se usa mais logo a seguir, utilidades em baixo. Em ecrãs estreitos
 * passa a barra horizontal no fundo, ao alcance do polegar.
 */
export function Rail({ children, footer, label }: { children: ReactNode; footer?: ReactNode; label: string }) {
  return (
    <nav aria-label={label} className="flex shrink-0 items-center gap-1 overflow-x-auto bg-ink px-2 py-1.5 text-white md:w-[84px] md:flex-col md:overflow-x-visible md:overflow-y-auto md:px-2 md:py-3">
      <img src="/makila-simbolo.svg" alt="" className="hidden h-12 w-12 md:mb-4 md:block" />
      <div className="flex items-center gap-1 md:w-full md:flex-col">{children}</div>
      {footer && <div className="ml-auto flex items-center gap-1 md:mt-auto md:ml-0 md:w-full md:flex-col md:border-t md:border-white/10 md:pt-3">{footer}</div>}
    </nav>
  );
}

interface ItemProps {
  icon: LucideIcon; label: string; active?: boolean; disabled?: boolean; onClick?: () => void; href?: string;
  /** Tecla de atalho, mostrada na dica. */
  kbd?: string; ariaLabel?: string; badge?: number | null;
}

/** Destino principal: ícone e nome curto por baixo; o activo leva o traço vermelho à esquerda. */
export function RailItem({ icon: Icon, label, active, disabled, onClick, href, kbd, ariaLabel, badge }: ItemProps) {
  const cls = clsx(
    'relative flex h-14 w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-medium leading-none transition-colors md:h-16 md:w-full',
    active ? 'bg-white/10 text-white before:absolute before:inset-y-3 before:-left-2 before:hidden before:w-[3px] before:rounded-r before:bg-brand-500 md:before:block' : 'text-white/55 hover:bg-white/5 hover:text-white',
    disabled && 'pointer-events-none opacity-35',
  );
  const body = (
    <>
      <Icon size={22} strokeWidth={active ? 2.2 : 1.8} className={active ? 'text-brand-200' : undefined} />
      <span className="max-w-full truncate px-1">{label}</span>
      {!!badge && <span className="num absolute right-2 top-1.5 min-w-5 rounded-full bg-brand-500 px-1 text-center text-[10px] leading-5 text-white">{badge}</span>}
    </>
  );
  const title = kbd ? `${ariaLabel ?? label} (${kbd})` : ariaLabel;
  if (href) {
    return <a href={href} aria-label={ariaLabel} title={title} aria-current={active ? 'page' : undefined} onClick={(e) => { if (onClick) { e.preventDefault(); onClick(); } }} className={cls}>{body}</a>;
  }
  return <button type="button" aria-label={ariaLabel} title={title} aria-pressed={active || undefined} disabled={disabled} onClick={onClick} className={cls}>{body}</button>;
}

/** Utilidade: só o ícone (o nome fica na dica e no leitor de ecrã). */
export function RailTool({ icon: Icon, label, onClick, kbd, children }: { icon?: LucideIcon; label: string; onClick: () => void; kbd?: string; children?: ReactNode }) {
  return (
    <button type="button" aria-label={label} title={kbd ? `${label} (${kbd})` : label} onClick={onClick}
      className="grid h-12 w-12 shrink-0 place-items-center rounded-xl text-white/55 transition-colors hover:bg-white/5 hover:text-white md:mx-auto">
      {children ?? (Icon && <Icon size={20} strokeWidth={1.8} />)}
    </button>
  );
}

/** Segunda barra lateral: o que há dentro da secção escolhida na primeira. */
export function SidePanel({ title, subtitle, children, footer, className }: { title: string; subtitle?: string; children: ReactNode; footer?: ReactNode; className?: string }) {
  return (
    <aside className={clsx('hidden w-60 shrink-0 flex-col border-r border-base bg-[var(--surface)] lg:flex', className)}>
      <div className="px-5 pb-3 pt-5">
        <div className="font-display text-lg font-semibold leading-tight">{title}</div>
        {subtitle && <div className="mt-0.5 truncate text-xs text-muted">{subtitle}</div>}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">{children}</div>
      {footer && <div className="border-t border-base p-3">{footer}</div>}
    </aside>
  );
}

export function PanelItem({ active, onClick, href, children, trailing, color, icon: Icon }: { active?: boolean; onClick: () => void; href?: string; children: ReactNode; trailing?: ReactNode; color?: string | null; icon?: LucideIcon }) {
  const cls = clsx('flex h-12 w-full items-center gap-3 rounded-lg px-3 text-left text-[15px] transition-colors',
    active ? 'bg-brand-50 font-semibold text-brand-800 dark:bg-white/10 dark:text-white' : 'text-[var(--text)] hover:bg-surface-2');
  const body = (
    <>
      {Icon && <Icon size={18} strokeWidth={active ? 2.2 : 1.8} className={clsx('shrink-0', active ? 'text-brand-700 dark:text-brand-200' : 'text-muted')} />}
      {color !== undefined && <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color ?? 'var(--border)' }} />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing !== undefined && <span className="num shrink-0 text-xs text-muted">{trailing}</span>}
    </>
  );
  return href
    ? <a href={href} aria-current={active ? 'page' : undefined} onClick={(e) => { e.preventDefault(); onClick(); }} className={cls}>{body}</a>
    : <button type="button" aria-pressed={active} onClick={onClick} className={cls}>{body}</button>;
}
